import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { deleteToken } from '../../apis/sessionTokenAPI';
import SidebarNav from '../../components/SidebarNav';
import '../main/mainPage.css';
import './toolsPage.css';

const capsuleFolderNameRegex = /^(?!.*[A-Z]{3})(((A0|Y0)[A-HJ-NP-Z0-9]{4})|((?!A0|Y0)[AC-HJ-NP-RT-Y][AC-HJ-NP-RT-Y0-9][AC-HJ-NP-RT-Y0-9]{4}))\.\d{3}$/i;

const ToolsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const retranscodeFolderInputRef = useRef(null);

  const [currentUser] = useState(() => {
    if (location.state && location.state.currentUser) {
      localStorage.setItem('currentUser', JSON.stringify(location.state.currentUser));
      return location.state.currentUser;
    }

    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch (error) {
        console.error('Error parsing saved user data:', error);
      }
    }

    return {};
  });

  const [showRetranscodeFolderModal, setShowRetranscodeFolderModal] = useState(false);
  const [retranscodeFolderFiles, setRetranscodeFolderFiles] = useState([]);
  const [retranscodeFolderName, setRetranscodeFolderName] = useState('');
  const [retranscodeFolderError, setRetranscodeFolderError] = useState('');
  const [isRetranscodeFolderUploading, setIsRetranscodeFolderUploading] = useState(false);
  const [isRetranscodeDropActive, setIsRetranscodeDropActive] = useState(false);

  const handleHome = () => {
    navigate('/main', { state: { currentUser } });
  };

  const handleCharts = () => {
    navigate('/charts', { state: { currentUser } });
  };

  const handleTools = () => {
    navigate('/tools', { state: { currentUser } });
  };

  const handleUser = () => {
    if (['admin', 'distributorAdmin'].includes(currentUser.role)) {
      navigate('/admin-user-management', { state: { currentUser } });
    } else if (['reviewer', 'distributorRegular'].includes(currentUser.role)) {
      navigate('/reviewer-user-management', { state: { currentUser } });
    }
  };

  const logOut = async () => {
    try {
      navigate('/login');
      await deleteToken();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const validateRetranscodeFolderSelection = (filesList) => {
    const filesArray = Array.from(filesList || []);
    const binFiles = filesArray.filter((file) => file.name?.toLowerCase().endsWith('.bin'));
    const relativePaths = filesArray
      .map((file) => file.webkitRelativePath || '')
      .filter(Boolean);
    const folderNames = [...new Set(relativePaths.map((path) => path.split('/')[0]).filter(Boolean))];
    const folderName = folderNames[0] || '';

    if (!folderName || folderNames.length !== 1 || !capsuleFolderNameRegex.test(folderName)) {
      return {
        valid: false,
        folderName,
        filesArray,
        message: 'Please upload a valid capsule folder.',
      };
    }

    if (binFiles.length < 3) {
      return {
        valid: false,
        folderName,
        filesArray,
        message: 'Please upload a capsule folder containing at least three .bin files.',
      };
    }

    return {
      valid: true,
      folderName,
      filesArray,
      message: '',
    };
  };

  const applyRetranscodeFolderSelection = (filesList) => {
    const result = validateRetranscodeFolderSelection(filesList);
    setRetranscodeFolderFiles(result.filesArray);
    setRetranscodeFolderName(result.folderName);
    setRetranscodeFolderError(result.message);
    return result;
  };

  const openRetranscodeFolderModal = () => {
    setRetranscodeFolderFiles([]);
    setRetranscodeFolderName('');
    setRetranscodeFolderError('');
    setShowRetranscodeFolderModal(true);
  };

  const closeRetranscodeFolderModal = () => {
    setShowRetranscodeFolderModal(false);
    setRetranscodeFolderFiles([]);
    setRetranscodeFolderName('');
    setRetranscodeFolderError('');
    setIsRetranscodeDropActive(false);
    if (retranscodeFolderInputRef.current) {
      retranscodeFolderInputRef.current.value = '';
    }
  };

  const handleRetranscodeFolderInputChange = (event) => {
    const result = applyRetranscodeFolderSelection(event.target.files);
    if (result.filesArray.length > 0 && !result.valid) {
      alert(result.message || 'Please upload a valid capsule folder.');
    }
  };

  const handleRetranscodeFolderDrop = (event) => {
    event.preventDefault();
    setIsRetranscodeDropActive(false);
    const result = applyRetranscodeFolderSelection(event.dataTransfer.files);
    if (result.filesArray.length > 0 && !result.valid) {
      alert(result.message || 'Please upload a valid capsule folder.');
    }
  };

  const uploadCapsuleFolderForRetranscodeToS3 = async ({ folderName, files }) => {
    // TODO: Replace this placeholder with a real backend API call that uploads the capsule folder to S3.
    console.log('TODO upload capsule folder for retranscode', {
      folderName,
      fileCount: files.length,
    });
    return { success: true };
  };

  const handleRetranscodeFolderSubmit = async () => {
    const result = validateRetranscodeFolderSelection(retranscodeFolderFiles);
    if (!result.valid) {
      setRetranscodeFolderError(result.message);
      alert(result.message || 'Please upload a valid capsule folder.');
      return;
    }

    try {
      setIsRetranscodeFolderUploading(true);
      await uploadCapsuleFolderForRetranscodeToS3({
        folderName: result.folderName,
        files: result.filesArray,
      });
      closeRetranscodeFolderModal();
      alert('Capsule folder upload request is ready (placeholder API called).');
    } catch (error) {
      console.error('Error uploading capsule folder for retranscode:', error);
      alert('Failed to upload capsule folder: ' + (error.message || 'Unknown error'));
    } finally {
      setIsRetranscodeFolderUploading(false);
    }
  };

  return (
    <div className="main-page-container tools-page-container">
      <SidebarNav
        activePage="tools"
        onHome={handleHome}
        onCharts={handleCharts}
        onTools={handleTools}
        onUsers={handleUser}
        onLogout={logOut}
      />

      <div className="main-content">
        <header className="header">
          <div>
            <h2 className="text-lg font-semibold">Welcome, {currentUser.first_name}</h2>
          </div>
        </header>

        <div className="container space-y-6">
          <h2 className="page-title">Tools</h2>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Retranscode Upload</h3>
            </div>
            <div className="card-body">
              <p className="tools-description">
                Upload one capsule folder with at least 3 <code>.bin</code> files. Folder name must match capsule SN format.
              </p>
              <button
                type="button"
                className="button"
                onClick={openRetranscodeFolderModal}
              >
                Upload to Retranscode
              </button>
            </div>
          </div>
        </div>
      </div>

      {showRetranscodeFolderModal && (
        <div className="note-modal">
          <div className="note-modal-content">
            <h2 className="card-title">Upload to Retranscode</h2>
            <p className="text-sm text-gray-600 mb-3">
              Upload one capsule folder with at least 3 <code>.bin</code> files. Folder name must match the capsule SN format.
            </p>

            <input
              ref={retranscodeFolderInputRef}
              type="file"
              webkitdirectory="true"
              directory=""
              multiple
              onChange={handleRetranscodeFolderInputChange}
              className="hidden"
            />

            <div
              role="button"
              tabIndex={0}
              onClick={() => retranscodeFolderInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  retranscodeFolderInputRef.current?.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setIsRetranscodeDropActive(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsRetranscodeDropActive(false);
              }}
              onDrop={handleRetranscodeFolderDrop}
              style={{
                border: `2px dashed ${isRetranscodeDropActive ? '#1976d2' : '#cbd5e1'}`,
                backgroundColor: isRetranscodeDropActive ? '#eff6ff' : '#f8fafc',
                borderRadius: '10px',
                padding: '20px',
                textAlign: 'center',
                cursor: 'pointer',
                marginBottom: '12px',
              }}
            >
              <div className="text-sm font-medium text-gray-700">
                Drag and drop a capsule folder here
              </div>
              <div className="text-xs text-gray-500 mt-1">
                or click to browse and select a folder
              </div>
            </div>

            <div className="text-sm text-gray-700 mb-2">
              <div><strong>Folder:</strong> {retranscodeFolderName || 'None selected'}</div>
              <div>
                <strong>.bin files:</strong>{' '}
                {retranscodeFolderFiles.filter((file) => file.name?.toLowerCase().endsWith('.bin')).length}
              </div>
            </div>

            {retranscodeFolderError && (
              <div className="text-sm text-red-600 mb-3">{retranscodeFolderError}</div>
            )}

            <div className="button-box">
              <button
                onClick={handleRetranscodeFolderSubmit}
                className="button"
                disabled={isRetranscodeFolderUploading}
              >
                {isRetranscodeFolderUploading ? 'Uploading...' : 'Upload Folder'}
              </button>
              <button
                onClick={closeRetranscodeFolderModal}
                className="button button-danger"
                disabled={isRetranscodeFolderUploading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolsPage;
